import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Clock, Users, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { companyInfo } from '../mock';

export const AboutPage = () => {
  const features = [
    {
      icon: Award,
      title: 'Excellence & Qualité',
      description: 'Nous mettons un point d’honneur à offrir des finitions impeccables et un travail de haute qualité.'
    },
    {
      icon: Clock,
      title: 'Respect des délais',
      description: 'Nous planifions chaque projet avec rigueur pour respecter les délais convenus.'
    },
    {
      icon: Users,
      title: 'Accompagnement personnalisé',
      description: 'De la première consultation à la réception des travaux, nous sommes à vos côtés.'
    },
    {
      icon: Target,
      title: 'Sérieux & Professionnalisme',
      description: 'Une équipe qualifiée et expérimentée pour mener à bien tous vos projets.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            À propos de nous
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            ISN Rénovation & Construction est votre partenaire de confiance pour tous vos projets de rénovation en Suisse.
          </p>
        </div>
      </section>

      {/* Company Description */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Notre expertise à votre service
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Que vous souhaitiez rénover un appartement, améliorer l’isolation de votre maison, refaire votre salle de bain ou entreprendre de grands travaux, ISN Rénovation & Construction met son savoir-faire et son expérience à votre disposition.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Nous intervenons sur tous types de chantiers, des petits travaux de peinture aux rénovations complètes, en passant par l’isolation, le carrelage, la toiture et bien plus encore.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Notre philosophie est simple : {companyInfo.motto}
              </p>
            </div>
            <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center">
              <p className="text-gray-400 text-center px-8">
                [Espace pour photo/vidéo de l'équipe ou d'un chantier]
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos valeurs</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ce qui nous distingue et fait notre réputation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-white" size={24} />
                    </div>
                    <CardTitle className="text-2xl text-gray-900 mb-3">{feature.title}</CardTitle>
                    <CardDescription className="text-gray-600 text-base leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Notre approche</h2>
          </div>

          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Écoute et conseil</h3>
                <p className="text-gray-600 leading-relaxed">
                  Nous prenons le temps de comprendre vos besoins, vos attentes et votre budget pour vous proposer des solutions adaptées.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Devis détaillé et transparent</h3>
                <p className="text-gray-600 leading-relaxed">
                  Nous vous fournissons un devis clair et précis, sans surprise ni coût caché.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Exécution soignée</h3>
                <p className="text-gray-600 leading-relaxed">
                  Nos équipes interviennent avec professionnalisme et veillent à la qualité de chaque détail.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-10 h-10 bg-gray-800 text-white rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Suivi et satisfaction</h3>
                <p className="text-gray-600 leading-relaxed">
                  Nous restons disponibles après la fin des travaux pour nous assurer de votre entière satisfaction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Travaillons ensemble sur votre projet
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Contactez-nous pour discuter de vos besoins et obtenir un devis personnalisé
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gray-800 hover:bg-gray-700 text-white">
              Demander un devis gratuit
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};